import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 40px 20px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #118c42 0%, #19a552 100%);
  padding: 30px 40px;
  text-align: center;
`;

const HeaderTitle = styled.h1`
  color: white;
  font-size: 28px;
  margin: 0 0 8px 0;
  font-weight: 600;
`;

const HeaderSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 16px;
  margin: 0;
`;

const Content = styled.div`
  padding: 40px;
`;

const ApplicationIdBadge = styled.div`
  background: #e8f5e9;
  border: 2px solid #28a745;
  border-radius: 10px;
  padding: 15px 20px;
  text-align: center;
  margin-bottom: 30px;
`;

const BadgeLabel = styled.span`
  color: #666;
  font-size: 14px;
  display: block;
  margin-bottom: 5px;
`;

const BadgeValue = styled.span`
  color: #118c42;
  font-size: 18px;
  font-weight: 700;
  font-family: monospace;
`;

const ImportantBox = styled.div`
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  border: 2px solid #ff9800;
  border-radius: 12px;
  padding: 25px;
  margin-bottom: 30px;
`;

const ImportantTitle = styled.h2`
  color: #e65100;
  font-size: 20px;
  margin: 0 0 15px 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ImportantText = styled.p`
  color: #333;
  font-size: 15px;
  line-height: 1.7;
  margin: 0;
`;

const StepsContainer = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h3`
  color: #118c42;
  font-size: 18px;
  margin: 0 0 20px 0;
  padding-bottom: 10px;
  border-bottom: 2px solid #e0e0e0;
`;

const StepCard = styled.div`
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px 25px;
  margin-bottom: 15px;
  border-left: 4px solid #118c42;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateX(5px);
  }
`;

const StepNumber = styled.span`
  background: #118c42;
  color: white;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  margin-right: 12px;
`;

const StepTitle = styled.h4`
  color: #333;
  font-size: 16px;
  margin: 0 0 10px 0;
  display: flex;
  align-items: center;
`;

const StepContent = styled.div`
  color: #555;
  font-size: 14px;
  line-height: 1.7;
  margin-left: 40px;
`;

const DocumentList = styled.ul`
  margin: 10px 0 0 0;
  padding-left: 20px;
  
  li {
    margin-bottom: 8px;
    color: #444;
  }
`;

const ContactBox = styled.div`
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-radius: 12px;
  padding: 25px;
  text-align: center;
  margin-bottom: 30px;
`;

const ContactTitle = styled.h3`
  color: #1565c0;
  font-size: 18px;
  margin: 0 0 15px 0;
`;

const ContactEmail = styled.a`
  color: #0d47a1;
  font-size: 20px;
  font-weight: 700;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ContactPhone = styled.p`
  color: #1565c0;
  font-size: 15px;
  margin: 10px 0 0 0;
`;

const WarningBox = styled.div`
  background: #ffebee;
  border: 2px solid #ef5350;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  margin-bottom: 30px;
`;

const WarningText = styled.p`
  color: #c62828;
  font-size: 15px;
  font-weight: 500;
  margin: 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  flex-wrap: wrap;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 14px 30px;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
  
  ${props => props.variant === 'primary' ? `
    background: linear-gradient(145deg, #118c42, #0d7a3a);
    color: white;
    &:hover {
      background: linear-gradient(145deg, #0d7a3a, #0a6830);
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(17, 140, 66, 0.3);
    }
  ` : `
    background: linear-gradient(145deg, #6c757d, #5a6268);
    color: white;
    &:hover {
      background: linear-gradient(145deg, #5a6268, #495057);
    }
  `}
`;

const NextStepsPage: React.FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();

  return (
    <PageWrapper>
      <Container>
        <Header>
          <HeaderTitle>🎉 What's Next?</HeaderTitle>
          <HeaderSubtitle>Complete your CNG Dealership Application</HeaderSubtitle>
        </Header>
        
        <Content>
          <ApplicationIdBadge>
            <BadgeLabel>Your Application ID</BadgeLabel>
            <BadgeValue>{applicationId || 'N/A'}</BadgeValue>
          </ApplicationIdBadge>
          
          <ImportantBox>
            <ImportantTitle>
              ⚠️ IMPORTANT - ACTION REQUIRED
            </ImportantTitle>
            <ImportantText>
              Your online application and payment are complete. However, to finalize your CNG Dealership application, 
              you <strong>MUST visit the MNGL Head Office in person</strong> for document verification. 
              This is a mandatory step and your application will only be processed after this verification.
            </ImportantText>
          </ImportantBox>
          
          <StepsContainer>
            <SectionTitle>📋 Documents to Bring</SectionTitle>
            
            <StepCard>
              <StepTitle>
                <StepNumber>1</StepNumber>
                Printed Copies
              </StepTitle>
              <StepContent>
                <DocumentList>
                  <li><strong>Application Form PDF</strong> - Download from your dashboard</li>
                  <li><strong>Payment Receipt PDF</strong> - Download from Thank You page or your email</li>
                </DocumentList>
              </StepContent>
            </StepCard>
            
            <StepCard>
              <StepTitle>
                <StepNumber>2</StepNumber>
                Original Documents
              </StepTitle>
              <StepContent>
                Carry all <strong>original documents</strong> as mentioned in the <strong>Checklist on Page 4</strong> of your Application Form PDF. These will be verified at the Head Office.
              </StepContent>
            </StepCard>
            
            <StepCard>
              <StepTitle>
                <StepNumber>3</StepNumber>
                Photocopies
              </StepTitle>
              <StepContent>
                Bring <strong>one complete set of photocopies</strong> of all documents to be submitted at the Head Office.
              </StepContent>
            </StepCard>
          </StepsContainer>
          
          <ContactBox>
            <ContactTitle>📍 MNGL Head Office Contact</ContactTitle>
            <ContactEmail href="mailto:cngdealership2026@mngl.in">
              cngdealership2026@mngl.in
            </ContactEmail>
            <ContactPhone>
              📞 Phone: 020 - 25584000 | FAX: 020 - 25511522
            </ContactPhone>
            <ContactPhone 
              as="a"
              href="https://share.google/oGoaFGHs0JBzGvjm1"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e: React.MouseEvent) => {
                // Check if mobile/tablet device
                const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                if (isMobile) {
                  e.preventDefault();
                  // Try to open in Google Maps app, fallback to browser
                  window.location.href = 'https://maps.google.com/?q=2nd+Floor,+Pride+Purple+Coronet,+Baner+Rd,+Riviresa+Society,+Baner,+Pune,+Maharashtra+411069';
                }
              }}
              style={{ 
                marginTop: '15px', 
                fontSize: '13px', 
                color: '#4a6cf7', 
                textDecoration: 'underline',
                cursor: 'pointer',
                display: 'block'
              }}
            >
              📍 2nd Floor, Pride Purple Coronet, Baner Rd, Riviresa Society, Baner, Pune, Maharashtra 411069
            </ContactPhone>
          </ContactBox>
          
          <WarningBox>
            <WarningText>
              ⚡ Your application will only be processed after document verification at the MNGL Head Office. 
              Please complete this step at your earliest convenience.
            </WarningText>
          </WarningBox>
          
          <ButtonGroup>
            <Button variant="primary" onClick={() => navigate('/user-dashboard')}>
              Go to Dashboard
            </Button>
            <Button variant="secondary" onClick={() => navigate(`/thank-you/${applicationId}`)}>
              ← Back to Thank You Page
            </Button>
          </ButtonGroup>
        </Content>
      </Container>
    </PageWrapper>
  );
};

export default NextStepsPage;
